import { useState, useEffect, useRef } from "react";
import { PageSEO } from "@/components/seo/PageSEO";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Send, Mail, MapPin, CalendarCheck, MessageSquare, Phone } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { supabase } from "@/integrations/supabase/client";

const contactSchema = z.object({
  company: z.string().trim().min(1, "Fältet är obligatoriskt").max(100),
  name: z.string().trim().min(1, "Fältet är obligatoriskt").max(100),
  email: z.string().trim().email("Ange en giltig e-postadress").max(255),
  role: z.string().trim().max(100).optional(),
  phone: z.string().trim().max(30).optional(),
  interest: z.string().min(1, "Välj ett alternativ"),
  message: z.string().trim().max(2000).optional(),
  contactCategory: z.enum(["general", "support"]).optional(),
});

type ContactFormData = z.infer<typeof contactSchema>;

type FormType = "demo" | "contact";

const Kontakt = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [formType, setFormType] = useState<FormType>(
    searchParams.get("type") === "demo" ? "demo" : "contact"
  );
  const [contactCategory, setContactCategory] = useState<"general" | "support">("general");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const mountedAt = useRef(Date.now());

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      company: "",
      name: "",
      email: "",
      role: "",
      phone: "",
      interest: "",
      message: "",
      contactCategory: "general",
    },
  });

  // Reset interest when switching type
  useEffect(() => {
    form.setValue("interest", "");
  }, [formType, form]);

  // Read type from URL on mount
  useEffect(() => {
    const type = searchParams.get("type");
    if (type === "demo") {
      setFormType("demo");
    }
  }, [searchParams]);

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.from("contact_submissions").insert({
        type: formType,
        company: data.company,
        name: data.name,
        email: data.email,
        role: data.role || null,
        interest: data.interest,
        message: data.message || null,
        phone: data.phone || null,
      });

      if (error) throw error;

      // Send email via edge function
      await supabase.functions.invoke("send-contact-email", {
        body: {
          type: formType,
          contactCategory: formType === "contact" ? contactCategory : undefined,
          company: data.company,
          name: data.name,
          email: data.email,
          role: data.role || undefined,
          interest: data.interest,
          phone: data.phone || undefined,
          message: data.message || undefined,
          honeypot,
          _elapsed: Date.now() - mountedAt.current,
        },
      });

      toast.success(t("kontakt.form.success"));
      form.reset({
        company: "",
        name: "",
        email: "",
        role: "",
        phone: "",
        interest: "",
        message: "",
        contactCategory: "general",
      });
    } catch (err) {
      console.error("Contact form submission error:", err);
      toast.error(t("kontakt.form.error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <PageSEO
        title={t("nav.kontakt")}
        description={t("seo.kontakt")}
        structuredData={{
          "@type": "ContactPage",
          name: "Kontakta Geposit",
          description: t("seo.kontakt"),
          mainEntity: {
            "@type": "Organization",
            name: "Geposit",
            url: "https://geposit.se",
            email: "info@geposit.se",
            address: {
              "@type": "PostalAddress",
              addressCountry: "SE",
            },
          },
        }}
      />
      {/* Hero */}
      <section className="relative bg-primary text-primary-foreground py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary/80" />
        <div className="absolute top-10 right-10 w-64 h-64 rounded-full border border-primary-foreground/5" />
        <div className="absolute bottom-0 left-1/4 w-96 h-96 rounded-full border border-primary-foreground/5 translate-y-1/2" />

        <div className="relative container mx-auto px-4 lg:px-8 text-center max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium font-heading leading-tight tracking-tight">
              {t("kontakt.hero.title")}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-primary-foreground/80 font-body leading-relaxed">
              {t("kontakt.hero.subtitle")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Type selector + Form */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 max-w-6xl mx-auto">
            {/* Left column: Type cards + contact info */}
            <div className="lg:col-span-2 space-y-8">
              {/* Type selector cards */}
              <div className="space-y-4" role="radiogroup" aria-label={t("kontakt.hero.title")}>
                <button
                  onClick={() => setFormType("demo")}
                  aria-pressed={formType === "demo"}
                  className={`w-full text-left rounded-xl border-2 p-5 transition-all duration-200 ${
                    formType === "demo"
                      ? "border-coral bg-coral/5 shadow-md"
                      : "border-border hover:border-coral/30 hover:shadow-sm"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      formType === "demo" ? "bg-coral text-coral-foreground" : "bg-secondary text-muted-foreground"
                    }`}>
                      <CalendarCheck className="h-5 w-5" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="font-bold font-subheading text-foreground">
                        {t("kontakt.types.demo.title")}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1 font-body">
                        {t("kontakt.types.demo.description")}
                      </p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setFormType("contact")}
                  aria-pressed={formType === "contact"}
                  className={`w-full text-left rounded-xl border-2 p-5 transition-all duration-200 ${
                    formType === "contact"
                      ? "border-coral bg-coral/5 shadow-md"
                      : "border-border hover:border-coral/30 hover:shadow-sm"
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      formType === "contact" ? "bg-coral text-coral-foreground" : "bg-secondary text-muted-foreground"
                    }`}>
                      <MessageSquare className="h-5 w-5" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="font-bold font-subheading text-foreground">
                        {t("kontakt.types.contact.title")}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1 font-body">
                        {t("kontakt.types.contact.description")}
                      </p>
                    </div>
                  </div>
                </button>
              </div>

              {/* Contact info */}
              <div className="bg-secondary/50 rounded-xl p-6 space-y-4">
                <h3 className="font-bold font-subheading text-foreground text-sm uppercase tracking-wider">
                  {t("kontakt.contactInfo.title")}
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-sm text-muted-foreground font-body">
                    <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-coral" />
                    {t("footer.address")}
                  </li>
                  <li>
                    <a
                      href={`mailto:${t("footer.email")}`}
                      className="flex items-center gap-3 text-sm text-muted-foreground hover:text-coral transition-colors font-body"
                    >
                      <Mail className="h-4 w-4 shrink-0 text-coral" />
                      {t("footer.email")}
                    </a>
                  </li>
                  <li>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="flex items-center gap-3 text-sm text-muted-foreground hover:text-coral transition-colors font-body">
                          <Phone className="h-4 w-4 shrink-0 text-coral" />
                          Kontakta säljare
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-64" side="top">
                        <div className="space-y-1">
                          <p className="font-bold font-subheading text-sm text-foreground">Max Andersson</p>
                          <p className="text-xs text-muted-foreground font-body">Säljare</p>
                          <a
                            href="tel:+46720806163"
                            className="flex items-center gap-2 text-sm text-coral hover:underline font-body mt-2"
                          >
                            <Phone className="h-3.5 w-3.5" />
                            +46 72 080 61 63
                          </a>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </li>
                </ul>
              </div>
            </div>

            {/* Right column: Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-3"
            >
              <div className="bg-card rounded-2xl border border-border p-6 sm:p-8 shadow-lg">
                <h2 className="text-2xl font-medium font-heading text-primary mb-2">
                  {formType === "demo"
                    ? t("kontakt.form.titleDemo")
                    : t("kontakt.form.titleContact")}
                </h2>
                <p className="text-sm text-muted-foreground font-body mb-6">
                  {formType === "demo"
                    ? t("kontakt.form.subtitleDemo")
                    : t("kontakt.form.subtitleContact")}
                </p>

                {formType === "contact" && (
                  <div className="flex rounded-lg border border-border overflow-hidden mb-6" role="group" aria-label="Kontaktkategori">
                    <button
                      type="button"
                      onClick={() => setContactCategory("general")}
                      aria-pressed={contactCategory === "general"}
                      className={`flex-1 py-2.5 px-4 text-sm font-medium transition-colors min-h-[44px] ${
                        contactCategory === "general"
                          ? "bg-coral text-coral-foreground"
                          : "bg-secondary text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {t("kontakt.types.contact.general")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setContactCategory("support")}
                      aria-pressed={contactCategory === "support"}
                      className={`flex-1 py-2.5 px-4 text-sm font-medium transition-colors min-h-[44px] ${
                        contactCategory === "support"
                          ? "bg-coral text-coral-foreground"
                          : "bg-secondary text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {t("kontakt.types.contact.support")}
                    </button>
                  </div>
                )}

                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                    {/* Honeypot field - hidden from real users */}
                    <div style={{ position: "absolute", left: "-9999px" }} aria-hidden="true">
                      <input
                        type="text"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                        value={honeypot}
                        onChange={(e) => setHoneypot(e.target.value)}
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="company"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("cta.form.company")}</FormLabel>
                            <FormControl>
                              <Input placeholder={t("kontakt.form.placeholders.company")} {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("cta.form.name")}</FormLabel>
                            <FormControl>
                              <Input placeholder={t("kontakt.form.placeholders.name")} {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("cta.form.email")}</FormLabel>
                            <FormControl>
                              <Input
                                type="email"
                                placeholder={t("kontakt.form.placeholders.email")}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="role"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("cta.form.role")}</FormLabel>
                            <FormControl>
                              <Input placeholder={t("kontakt.form.placeholders.role")} {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("kontakt.form.phone")}</FormLabel>
                          <FormControl>
                            <Input
                              type="tel"
                              placeholder={t("kontakt.form.placeholders.phone")}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="interest"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {formType === "demo"
                              ? t("kontakt.form.demoInterestLabel")
                              : t("kontakt.form.interestLabel")}
                          </FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder={t("kontakt.form.placeholders.interest")} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {formType === "demo" ? (
                                <>
                                  <SelectItem value="atlas">Atlas</SelectItem>
                                  <SelectItem value="valid">Valid</SelectItem>
                                  <SelectItem value="adresstwatt">Adresstvätt</SelectItem>
                                  <SelectItem value="fler">Fler tjänster</SelectItem>
                                </>
                              ) : (
                                <>
                                  <SelectItem value="atlas">Atlas - Kartverktyg</SelectItem>
                                  <SelectItem value="valid">Valid - Adressvalidering</SelectItem>
                                  <SelectItem value="adresstwatt">Adresstvätt</SelectItem>
                                  <SelectItem value="general">Övriga frågor</SelectItem>
                                </>
                              )}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("cta.form.message")}</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder={
                                formType === "demo"
                                  ? t("kontakt.form.placeholders.messageDemo")
                                  : t("kontakt.form.placeholders.messageContact")
                              }
                              rows={4}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      size="lg"
                      disabled={isSubmitting}
                      className="w-full bg-coral text-coral-foreground hover:bg-coral/90 font-semibold shadow-lg"
                    >
                      <Send className="mr-2 h-4 w-4" />
                      {isSubmitting
                        ? t("kontakt.form.submitting")
                        : formType === "demo"
                          ? t("kontakt.form.submitDemo")
                          : t("cta.form.submit")}
                    </Button>
                  </form>
                </Form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Kontakt;
